/** Ground. The old town sits in the northwest corner; the country runs east and south. */
export const TILE = 16;
export const MAP_W = 560;
/** Rows below DRAWN_H are past where the map was drawn: sealed places the story opens. */
export const MAP_H = 360;
/** The country everyone can walk to. The seed only grows this much. */
export const DRAWN_H = 300;
/** Rows added north of the original town so the well has somewhere to lead. */
export const NORTH = 14;
/** The hand-built part of the map. Everything past it is grown from the seed. */
export const OLD_W = 378;
export const OLD_H = 132;

export const T_GRASS = 0;
export const T_DIRT = 1;
export const T_ROAD = 2;
export const T_DINER = 3;
export const T_ROCK = 4;
export const T_WATER = 5;
export const T_STATION = 6;
export const T_COUNTER = 7;
/** Solid, but drawn as the floor under a prop sprite. */
export const T_PROP = 8;
export const T_SALT = 9;
export const T_CRUST = 10;
/** Walkable pale glass. The lamp rows. */
export const T_GLASS = 11;
/** Walkable black seep. The grease cut. */
export const T_GREASE = 12;
/** Dull track between places. */
export const T_TRACK = 13;
/** Walkable pale metal-dust. The antenna court. */
export const T_STATIC = 14;
/** Solid copper wall. */
export const T_COPPER = 15;
/** Walkable bone dirt. The marrow yard. */
export const T_MARROW = 16;
/** Solid rib wall. */
export const T_RIB = 17;
/** Walkable shine. It takes a little health while you stand in it. */
export const T_GLOW = 18;
/** Walkable pool. It bites if you walk through it. */
export const T_TOXIC = 19;
/** Walkable cracked grey. The kettle flats. */
export const T_ASH = 20;
/** Solid glass that grew in points. The brights. */
export const T_SHARD = 21;
/** Solid dead tree. */
export const T_TREE = 22;
/** Walkable dry brush. */
export const T_SCRUB = 23;
/** Walkable vent. It breathes on you. */
export const T_VENT = 24;
/** Walkable poured floor. Ruins, Sump, the dark station. */
export const T_FLOOR = 25;
/** Solid poured wall. */
export const T_WALL = 26;
/** Walkable mud. It holds your boots. */
export const T_MUD = 27;
/** Walkable bare paper. Ground nobody got round to painting. */
export const T_BLANK = 28;
/** Solid pencil line. A wall somebody sketched and never finished. */
export const T_SKETCH = 29;

export function solid(tile: number): boolean {
  return (
    tile === T_DINER ||
    tile === T_ROCK ||
    tile === T_WATER ||
    tile === T_STATION ||
    tile === T_COUNTER ||
    tile === T_PROP ||
    tile === T_CRUST ||
    tile === T_COPPER ||
    tile === T_RIB ||
    tile === T_SHARD ||
    tile === T_TREE ||
    tile === T_WALL ||
    tile === T_SKETCH
  );
}

/** Wall tiles get a dark edge so they do not melt into the ground. Trees and props are drawn as sprites instead. */
export function isWall(tile: number): boolean {
  return (
    tile === T_DINER ||
    tile === T_STATION ||
    tile === T_COUNTER ||
    tile === T_ROCK ||
    tile === T_CRUST ||
    tile === T_COPPER ||
    tile === T_RIB ||
    tile === T_SHARD ||
    tile === T_WALL ||
    tile === T_SKETCH
  );
}

/** Seeded, so the client and server grow the same country. */
export function rng32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function hash2(x: number, y: number, seed = 0): number {
  let n = Math.imul(x, 374761393) + Math.imul(y, 668265263) + Math.imul(seed, 2147483647);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
}

/** Smooth value noise, 0..1. */
export function noise(x: number, y: number, scale: number, seed: number): number {
  const fx = x / scale;
  const fy = y / scale;
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const tx = fx - x0;
  const ty = fy - y0;
  const sx = tx * tx * (3 - 2 * tx);
  const sy = ty * ty * (3 - 2 * ty);
  const a = hash2(x0, y0, seed);
  const b = hash2(x0 + 1, y0, seed);
  const c = hash2(x0, y0 + 1, seed);
  const d = hash2(x0 + 1, y0 + 1, seed);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}
